import React from "react";

interface PromptItem {
  title: string;
  description: string;
  prompt: string;
}

function DefaultPrompt({ onClick }: { onClick: (data: string) => void }) {
  const prompts: PromptItem[] = [
    {
      title: "Quelles sont les approches de gestion de projet ?",
      description:
        "Description des différentes approches ou systèmes de pratiques les plus populaires en gestion de projet.",
      prompt:
        "Présentez les principales méthodes en gestion de projet (agiles, classiques, hybrides), avec des exemples pour chaque méthode (Scrum, Kanban, Waterfall, Prince2, etc.). Expliquez brièvement les avantages et les contextes d'utilisation pour chaque méthode. Terminez en suggérant des critères pour choisir la méthode la plus adaptée selon le type de projet (exemple : flexibilité, clarté des besoins, structure).",
    },
    {
      title: "Stratégies de gestion des risques",
      description:
        "Les stratégies d'identification et d'évaluation des risques inhérents à un projet, permettant de mitiger et gérer leur impact sur ce dernier.",
      prompt:
        "Comment élaborer une stratégie efficace de gestion des risques dans un projet? Décrivez un processus structuré qui inclut l’identification, l’évaluation, et la priorisation des risques, ainsi que les principales stratégies de réponse telles que l’élimination, l’atténuation, le transfert, ou l’acceptation des risques. Expliquez également comment intégrer ces étapes dans un plan de gestion des risques et assurer un suivi continu tout au long du cycle de vie du projet.",
    },
    {
      title: "Comment définir les indicateurs de performance d’un projet",
      description:
        "Le suivi de projet par des KPI (indicateurs de performance) est essentiel pour évaluer l'avancement, la performance et la qualité des actions entreprises.",
      prompt:
        "Comment définir les indicateurs clés de performance (KPI) d’un projet ? Expliquez comment identifier les objectifs du projet et sélectionner des KPI adaptés aux différentes catégories telles que les coûts, les délais, la productivité, les risques et la satisfaction des parties prenantes. Précisez également comment organiser leur suivi dans un tableau de bord et adapter ces indicateurs en fonction de l’évolution du projet.",
    },
    {
      title: "Comment recadrer un projet qui part en dérive en termes de délais",
      description:
        "Un projet qui dérape est un projet qui risque de ne pas atteindre les résultats escomptés. Recadrer consiste à mettre en place une stratégie pour remettre sur les rails, un projet qui dérape.",
      prompt:
        "Comment recadrer un projet en dérive sur les délais ? Proposez une approche en 4 étapes : identifier les problèmes à l’origine des écarts, réaliser un état des lieux des travaux et ressources, définir une stratégie adaptée pour rattraper les délais, et enfin mettre en œuvre un plan d’action détaillé avec un suivi rigoureux. Fournissez une réponse claire et pratique.",
    },
  ];

  return (
    <div className="w-full">
      {/* 2x2 Grid of Prompt Cards with uniform height across all cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-fr [grid-auto-rows:1fr]">
        {prompts.map((item, index) => (
          <div
            key={index}
            onClick={() => onClick(item.prompt)}
            className="group relative p-5 flex flex-col justify-between h-full rounded-2xl cursor-pointer transition-all duration-300 bg-white/70 dark:bg-[#131622]/80 backdrop-blur-md border border-slate-200/80 dark:border-white/10 hover:border-purple-500/50 dark:hover:border-purple-500/40 hover:bg-white dark:hover:bg-[#181c2b] shadow-sm hover:shadow-glow-purple"
          >
            <div className="flex flex-col h-full justify-between">
              {/* Top row: Title + Arrow icon */}
              <div className="flex items-start justify-between gap-3 mb-2.5">
                <h3 className="font-display font-medium text-sm text-slate-800 dark:text-slate-100 group-hover:text-purple-600 dark:group-hover:text-purple-300 transition-colors leading-snug">
                  {item.title}
                </h3>
                <div className="w-7 h-7 min-w-[1.75rem] rounded-full flex items-center justify-center bg-slate-100 dark:bg-white/5 group-hover:bg-purple-600 group-hover:text-white text-slate-400 dark:text-slate-400 transition-all duration-300 shrink-0">
                  <svg
                    className="w-3.5 h-3.5 transform group-hover:translate-x-0.5 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2.2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                    />
                  </svg>
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-sans mt-auto">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DefaultPrompt;
